/**
 * MobileIncidentsList — phone / tablet ( <lg ) presentation of the
 * Incidents & Safeguarding list. Read-only card list that navigates to
 * the existing `/incidents/{id}` route for anything beyond a glance.
 *
 * Preserves every existing API, permission, tenancy filter and audit
 * behaviour — nothing here writes to the server. Card priority (top→
 * bottom) follows the reviewer's brief: Severity → Safeguarding /
 * Overdue → Reference → Service User → Category → Date/Time →
 * Site/Location → Status.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ShieldAlert, ClipboardList, AlertOctagon, Activity, CheckCircle2, Building2, Clock, Search, Filter, Download, RefreshCw, Plus, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import StatusPill from "@/components/StatusPill";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const KPI_META = {
  open: { label: "Open", Icon: AlertOctagon, tone: "text-amber-700" },
  under_investigation: { label: "Under Investigation", Icon: Activity, tone: "text-sky-700" },
  safeguarding: { label: "Safeguarding", Icon: ShieldAlert, tone: "text-red-700" },
  overdue_actions: { label: "Overdue Actions", Icon: ClipboardList, tone: "text-rose-700" },
  closed_this_month: { label: "Closed / month", Icon: CheckCircle2, tone: "text-emerald-700" },
};

function fmtDate(v) { if (!v) return "—"; return String(v).slice(0, 16).replace("T", " "); }
function humanize(v) { if (!v) return "—"; return String(v).replace(/_/g, " "); }

// Only surface the safeguarding chip when there is an actual concern to
// convey. "not_applicable" (default) and empty values are suppressed.
function shouldShowSafeguarding(status) {
  return status && status !== "not_applicable";
}

export default function MobileIncidentsList({
  loading, rows, summary, siteById, activeCard, activeFilterCount,
  query, onQueryChange, onOpenFilters, onRefresh, onExport, onNewRecord,
  onCardClick, canManage,
}) {
  const navigate = useNavigate();

  return (
    <div
      className="lg:hidden flex-1 min-h-0 overflow-auto bg-slate-50 dark:bg-slate-950"
      data-testid="mobile-incidents"
    >
      <div className="px-3 pt-3 pb-6 space-y-3 max-w-full">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Clinical</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-slate-100 leading-tight" data-testid="mobile-incidents-title">
              Incidents &amp; Safeguarding
            </h2>
          </div>
          {canManage && (
            <Button
              onClick={onNewRecord}
              data-testid="mobile-incidents-new"
              className="h-11 bg-sky-700 hover:bg-sky-800 text-white shrink-0"
            >
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          )}
        </div>

        {/* Search + action row */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search…"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              data-testid="mobile-incidents-search"
              className="h-11 pl-9 bg-white dark:bg-slate-900"
            />
          </div>
          <Button
            variant="outline"
            onClick={onOpenFilters}
            data-testid="mobile-incidents-filters"
            className="h-11 shrink-0"
          >
            <Filter className="w-4 h-4 mr-1" /> Filters
            {activeFilterCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-sky-100 text-sky-800">{activeFilterCount}</span>
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                data-testid="mobile-incidents-more"
                className="inline-flex items-center justify-center w-11 h-11 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 shrink-0"
              >
                <MoreVertical className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">Actions</DropdownMenuLabel>
              <DropdownMenuItem onSelect={onRefresh} data-testid="mobile-incidents-refresh">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onExport} data-testid="mobile-incidents-export">
                <Download className="w-4 h-4 mr-2" /> Export PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* KPI grid — 2-up on phone */}
        <div className="grid grid-cols-2 gap-2" data-testid="mobile-incidents-kpis">
          {["open", "under_investigation", "safeguarding", "overdue_actions", "closed_this_month"].map((k) => {
            const meta = KPI_META[k];
            const Icon = meta.Icon;
            const active = activeCard === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => onCardClick(k)}
                data-testid={`mobile-card-${k}`}
                className={`min-h-[44px] text-left rounded-lg border bg-white dark:bg-slate-900 p-3 ${active ? "ring-2 ring-sky-500 border-sky-300" : "border-slate-200 dark:border-slate-800"}`}
              >
                <div className="flex items-center justify-between gap-1">
                  <div className="text-[9px] uppercase tracking-widest text-slate-600 dark:text-slate-400 font-semibold truncate">{meta.label}</div>
                  <Icon className={`w-4 h-4 shrink-0 ${meta.tone}`} />
                </div>
                <div className="text-2xl font-heading font-semibold tabular-nums text-slate-900 dark:text-slate-100 mt-0.5" data-testid={`mobile-card-${k}-value`}>
                  {summary?.[k] ?? "—"}
                </div>
              </button>
            );
          })}
        </div>

        {/* Card list */}
        {loading ? (
          <div className="text-sm text-slate-500 p-4" data-testid="mobile-incidents-loading">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center" data-testid="mobile-incidents-empty">
            <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">No incidents match the current filters.</div>
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => {
              const overdue = (r.actions || []).some((a) => a.status === "overdue");
              const siteName = siteById[r.site_id];
              const showSafeguarding = shouldShowSafeguarding(r.safeguarding_status);
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => navigate(`/incidents/${r.id}`)}
                  data-testid={`incident-row-${r.id}`}
                  className="w-full text-left rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 min-h-[44px] active:bg-slate-50 dark:active:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
                >
                  {/* Row 1: Severity + safeguarding/overdue warnings */}
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    <StatusPill value={r.severity} kind="severity" />
                    {showSafeguarding && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-md border bg-red-50 border-red-300 text-red-800 dark:bg-red-950/30 dark:border-red-700 dark:text-red-300"
                        data-testid={`mobile-incident-safeguarding-${r.id}`}
                      >
                        <ShieldAlert className="w-3 h-3" /> Safeguarding {r.safeguarding_status === "under_review" ? "· review" : ""}
                      </span>
                    )}
                    {overdue && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-md border bg-rose-50 border-rose-300 text-rose-800 dark:bg-rose-950/30 dark:border-rose-700 dark:text-rose-300"
                        data-testid={`mobile-incident-overdue-${r.id}`}
                      >
                        <ClipboardList className="w-3 h-3" /> Overdue action
                      </span>
                    )}
                    <div className="ml-auto"><ChevronRight className="w-4 h-4 text-slate-400" /></div>
                  </div>

                  {/* Row 2: Reference (mono) */}
                  <div className="font-mono text-[11px] text-slate-600 dark:text-slate-400">{r.reference}</div>

                  {/* Row 3: Service user */}
                  <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {r.client_name || <span className="text-slate-400 font-normal">Unspecified service user</span>}
                  </div>

                  {/* Row 4: Category + date */}
                  <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="capitalize font-medium">{humanize(r.category)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(r.occurred_at)}</span>
                  </div>

                  {/* Row 5: Site / location */}
                  {(siteName || r.location) && (
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 inline-flex items-center gap-1 truncate">
                      <Building2 className="w-3 h-3" /> {[siteName, r.location].filter(Boolean).join(" · ")}
                    </div>
                  )}

                  {/* Row 6: Status + reporter */}
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <StatusPill value={r.status} kind="generic" />
                    <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                      by {r.reported_by || r.created_by_name || "—"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
